// Admin-only monthly evaluation ("Auswertung"): revenue per customer→project,
// revenue per employee, projects completed in the period, CSV export, and a
// direct channel to reflect improvement potential back to the team.
import { useState, useMemo } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, fmtEuro, fmtDeadline, capitalizeName } from './lib/format.js';
import {
  buildMonthlyReport, completedInMonth, buildMonthlyCsv, downloadCsv,
  unbilledFromReport, monthLabel, monthKey, addMonths,
} from './lib/analytics.js';
import { Avatar, ProjectAvatar } from './components.jsx';
import { MentionText } from './mention-input.jsx';
import {
  IconChevronLeft, IconChevronRight, IconDownload, IconCheck, IconX, IconMessage,
} from './icons.jsx';
import { FeedbackModal } from './feedback-modal.jsx';

// Die Begründungen, die die Beteiligten beim Abschluss eines überzogenen
// Projekts hinterlegen mussten. Sie stehen genau dort, wo die Überziehung
// auffällt — sonst müsste die Geschäftsführung sie im Projekt zusammensuchen.
function ClosureReasons({ projectId }) {
  const { completionsByProject, profileById } = useData();
  const [open, setOpen] = useState(false);
  const reasons = completionsByProject[projectId] || [];
  if (reasons.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <button
        className="btn btn-ghost"
        onClick={() => setOpen(o => !o)}
        style={{ padding: '2px 6px', fontSize: 12 }}
      >
        {open ? 'Begründungen ausblenden' : `Begründungen ansehen (${reasons.length})`}
      </button>
      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          {reasons.map(c => {
            const author = profileById(c.user_id);
            return (
              <div key={c.id} style={{ borderLeft: '2px solid var(--crit)', paddingLeft: 10 }}>
                <div className="tiny" style={{ fontWeight: 600, color: 'var(--ink-2)' }}>
                  {author ? (capitalizeName(author.full_name) || author.email) : 'Unbekannt'}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {' · '}{new Date(c.updated_at || c.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </span>
                </div>
                <div className="tiny" style={{ marginTop: 3, color: 'var(--ink-2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  <MentionText text={c.reason}/>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const FILTERS = [
  { key: 'all',      label: 'Alle' },
  { key: 'billable', label: 'Abrechenbar' },
  { key: 'internal', label: 'Intern' },
];

// Compact hand-rolled donut (same approach as the dashboard).
function Donut({ slices, size = 180 }) {
  const total = slices.reduce((s, x) => s + x.value, 0);
  if (total === 0) return null;
  const r = size / 2, inner = r * 0.62, cx = r, cy = r;
  if (slices.length === 1) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill={slices[0].color}/>
        <circle cx={cx} cy={cy} r={inner} fill="var(--bg-card)"/>
      </svg>
    );
  }
  let acc = 0;
  const wedges = slices.map((s) => {
    const a0 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    acc += s.value;
    const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
    const x1 = cx + r * Math.cos(a0), y1 = cy + r * Math.sin(a0);
    const x2 = cx + r * Math.cos(a1), y2 = cy + r * Math.sin(a1);
    const large = (a1 - a0) > Math.PI ? 1 : 0;
    return <path key={s.id} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={s.color}/>;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {wedges}
      <circle cx={cx} cy={cy} r={inner} fill="var(--bg-card)"/>
    </svg>
  );
}

function Kpi({ label, value, sub, tone }) {
  return (
    <div className="card card-pad" style={{ flex: 1, minWidth: 0 }}>
      <div className="eyebrow">{label}</div>
      <div className="serif" style={{ fontSize: 26, fontWeight: 500, marginTop: 8, color: tone || 'var(--ink)' }}>{value}</div>
      {sub && <div className="tiny muted" style={{ marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const CUSTOMER_FALLBACK = '#6B7280';

function AnalyticsPage() {
  const { timeEntries, profiles, projects, customers, projectUsage, projectById, updateProject } = useData();

  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [filter, setFilter] = useState('all');
  const [openCustomer, setOpenCustomer] = useState(null); // expanded customer key
  const [feedbackCtx, setFeedbackCtx] = useState(null);    // props for FeedbackModal

  // Filtered report drives the breakdown panels + CSV; the unfiltered report
  // drives the KPI row + reminder so "Umsatz" always reflects the full month
  // (billable only) regardless of the active filter.
  const report = useMemo(
    () => buildMonthlyReport(timeEntries, profiles, projects, customers, { ...ym, filter }),
    [timeEntries, profiles, projects, customers, ym, filter]
  );
  const reportAll = useMemo(
    () => filter === 'all' ? report : buildMonthlyReport(timeEntries, profiles, projects, customers, { ...ym, filter: 'all' }),
    [timeEntries, profiles, projects, customers, ym, filter, report]
  );

  const completed = useMemo(
    () => completedInMonth(projects, customers, projectUsage, ym),
    [projects, customers, projectUsage, ym]
  );

  // End-of-month reminder: billable projects with revenue this month that
  // aren't marked invoiced yet. Only surfaces in the last week of the *current*
  // month while viewing it.
  const isCurrentMonth = ym.year === now.getFullYear() && ym.month === now.getMonth();
  const daysInMonth = new Date(ym.year, ym.month + 1, 0).getDate();
  const inLastWeek = now.getDate() > daysInMonth - 7;
  const unbilled = useMemo(() => unbilledFromReport(reportAll, projects), [reportAll, projects]);
  const showReminder = isCurrentMonth && inLastWeek && unbilled.length > 0;
  const unbilledTotal = unbilled.reduce((s, u) => s + u.euro, 0);

  function setBillable(projectId, billable) {
    updateProject(projectId, { billable }).catch(() => {});
  }
  function setInvoiced(projectId, invoiced) {
    updateProject(projectId, { invoiced, invoiced_at: invoiced ? new Date().toISOString() : null }).catch(() => {});
  }

  const { totals, perCustomer, perEmployee } = report;
  const kt = reportAll.totals; // KPI totals (full month, filter-independent)
  // Bars represent the OPEN (noch abzurechnen) revenue so they shrink as work is
  // invoiced — matching the bold figure next to them. Already-invoiced and
  // internal value are shown only as muted secondary figures.
  const openCust = (c) => Math.max(0, c.billableEuro - (c.invoicedEuro || 0));
  const openEmp = (e) => Math.max(0, e.billableEuro - (e.invoicedEuro || 0));
  const maxCustEuro = Math.max(...perCustomer.map(openCust), 1);
  const maxEmpEuro = Math.max(...perEmployee.map(openEmp), 1);

  const donutSlices = perCustomer
    .map((c) => ({ c, open: openCust(c) }))
    .filter(({ open }) => open > 0)
    .slice(0, 8)
    .map(({ c, open }) => ({
      id: c.id,
      value: open,
      color: c.customer?.color || CUSTOMER_FALLBACK,
      label: c.customer?.name || 'Ohne Kunde',
    }));

  function shift(delta) {
    setYm(prev => addMonths(prev.year, prev.month, delta));
    setOpenCustomer(null);
  }

  function handleExport() {
    const csv = buildMonthlyCsv(report);
    downloadCsv(`Auswertung_${monthKey(ym.year, ym.month)}.csv`, csv);
  }

  // Open the feedback composer pre-filled from a completed project's metrics.
  function feedbackForProject(row) {
    const involved = [...new Set(timeEntries
      .filter(te => te.project_id === row.project.id)
      .map(te => te.user_id))];
    const bits = [];
    if (row.budgetPct != null) bits.push(`${row.budgetPct}% Budget`);
    if (row.daysLate != null && row.daysLate > 0) bits.push(`${row.daysLate} Tage über Deadline`);
    const ctx = `${row.project.title}${bits.length ? ' · ' + bits.join(' · ') : ''}`;
    const cat = row.overBudget ? 'overtime' : (row.onTime === false ? 'overtime' : 'general');
    setFeedbackCtx({ presetRecipientIds: involved, project: row.project, context: ctx, defaultCategory: cat });
  }

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Auswertung</h1>
        <div className="seg" style={{ marginLeft: 4 }}>
          {FILTERS.map(f => (
            <button key={f.key} className={filter === f.key ? 'on' : ''} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <div className="topbar-spacer"/>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button className="icon-btn" onClick={() => shift(-1)} title="Vorheriger Monat"><IconChevronLeft size={14}/></button>
          <div className="mono" style={{ minWidth: 130, textAlign: 'center', fontSize: 13.5, fontWeight: 600 }}>
            {monthLabel(ym.year, ym.month)}
          </div>
          <button className="icon-btn" onClick={() => shift(1)} disabled={isCurrentMonth}
                  style={{ opacity: isCurrentMonth ? 0.4 : 1 }} title="Nächster Monat"><IconChevronRight size={14}/></button>
        </div>
        <button className="btn btn-primary" onClick={handleExport} disabled={totals.minutes === 0} style={{ marginLeft: 8 }}>
          <IconDownload size={13}/> CSV
        </button>
      </div>

      <div className="content">
        {/* End-of-month reminder: billable but not-yet-invoiced work */}
        {showReminder && (
          <div className="card card-pad-lg" style={{ marginBottom: 20, borderColor: 'rgba(245,158,11,0.4)', background: 'var(--warn-soft)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>🧾</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--warn)' }}>
                  Monatsende — noch nicht abgerechnet
                </span>
              </div>
              <span className="mono" style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>
                {unbilled.length} {unbilled.length === 1 ? 'Projekt' : 'Projekte'} · {fmtEuro(unbilledTotal)}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {unbilled.map(u => (
                <div key={u.project.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <ProjectAvatar project={u.project} size={26}/>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.project.title} <span className="tiny muted">· {u.customerName}</span>
                  </span>
                  <span className="tiny mono muted" style={{ whiteSpace: 'nowrap' }}>{fmtEuro(u.euro)}</span>
                  <button className="btn btn-ghost" onClick={() => setBillable(u.project.id, false)}
                          style={{ height: 28, border: '1px solid var(--stroke)' }} title="Projekt als nicht abrechenbar markieren">
                    <IconX size={12}/> nicht abrechenbar
                  </button>
                  <button className="btn btn-ghost" onClick={() => setInvoiced(u.project.id, true)}
                          style={{ height: 28, border: '1px solid var(--stroke)' }} title="Projekt als abgerechnet markieren">
                    <IconCheck size={12}/> abgerechnet
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPIs — always the full month picture (billable revenue), independent of the filter */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          <Kpi label="Umsatz (Monat)" value={fmtEuro(kt.openEuro)}
               sub={kt.invoicedEuro > 0
                 ? `noch offen · ${fmtEuro(kt.invoicedEuro)} schon abgerechnet`
                 : `abrechenbar · ${fmtMin(kt.billableMinutes)}`}
               tone="var(--accent)"/>
          <Kpi label="Abrechenbar" value={fmtMin(kt.billableMinutes)} sub={`${Math.round((kt.minutes ? kt.billableMinutes / kt.minutes : 0) * 100)}% der Stunden`} tone="var(--ok)"/>
          <Kpi label="Intern" value={fmtMin(kt.internalMinutes)} sub={`nicht abrechenbar · ${fmtEuro(kt.internalEuro)}`}/>
          <Kpi label="Abgeschlossen" value={completed.length} sub={`Projekte in ${monthLabel(ym.year, ym.month)}`}/>
        </div>

        {totals.minutes === 0 && (
          <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
            <div className="tiny muted">Für {monthLabel(ym.year, ym.month)} wurden keine Stunden im gewählten Filter erfasst.</div>
          </div>
        )}

        <div className="grid grid-12" style={{ marginBottom: 20 }}>
          {/* Revenue per customer → projects */}
          <div className="card col-7 card-pad-lg">
            <div className="card-title">Umsatz pro Kunde <span className="sub">aufklappen für Projekte</span></div>
            {perCustomer.length === 0 ? (
              <div className="tiny muted" style={{ marginTop: 14 }}>Keine Daten.</div>
            ) : (
              <div style={{ display: 'flex', gap: 24, marginTop: 16, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0 }}>
                  <Donut slices={donutSlices} size={170}/>
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {perCustomer.map(c => {
                    const open = openCustomer === c.id;
                    const color = c.customer?.color || CUSTOMER_FALLBACK;
                    const invoicedCount = c.projects.filter(p => {
                      const pr = p.projectId ? projectById(p.projectId) : null;
                      return pr && pr.invoiced;
                    }).length;
                    return (
                      <div key={c.id}>
                        <div
                          onClick={() => setOpenCustomer(open ? null : c.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                              <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }}/>
                              <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.customer?.name || 'Ohne Kunde'}
                              </span>
                              <span className="tiny muted">{c.projects.length} Pos.</span>
                              {invoicedCount > 0 && (
                                <span className="tiny" style={{ color: 'var(--ok)', whiteSpace: 'nowrap' }}>✓ {invoicedCount} abgerechnet</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                              <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{fmtEuro(openCust(c))}</span>
                              {c.invoicedEuro > 0 && (
                                <span className="mono tiny" style={{ color: 'var(--ok)' }} title="bereits abgerechnet">+{fmtEuro(c.invoicedEuro)} abgerechnet</span>
                              )}
                              {c.internalEuro > 0 && (
                                <span className="mono tiny muted" title="nicht abrechenbar">+{fmtEuro(c.internalEuro)} intern</span>
                              )}
                            </div>
                          </div>
                          <div className="bar" style={{ marginTop: 6 }}>
                            <span style={{ width: `${(openCust(c) / maxCustEuro) * 100}%`, background: color }}/>
                          </div>
                        </div>
                        {open && (
                          <div style={{ margin: '8px 0 4px', paddingLeft: 18, display: 'flex', flexDirection: 'column' }}>
                            {c.projects.map(p => {
                              const proj = p.projectId ? projectById(p.projectId) : null;
                              const internal = proj && proj.billable === false;
                              return (
                                <div key={p.id} style={{ padding: '7px 0', borderTop: '1px solid var(--stroke)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                    <span className="tiny" style={{ color: 'var(--ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                                      {proj?.invoiced && <span title="abgerechnet" style={{ color: 'var(--ok)', fontWeight: 700 }}>✓</span>}
                                      {p.label}
                                      {internal && <span className="chip" style={{ height: 17, fontSize: 9.5, padding: '0 6px' }}>intern</span>}
                                    </span>
                                    <span className="tiny mono muted" style={{ whiteSpace: 'nowrap' }}>{fmtMin(p.minutes)} · {fmtEuro(p.euro)}</span>
                                  </div>
                                  {proj && (
                                    <div style={{ display: 'flex', gap: 16, marginTop: 5 }}>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, color: 'var(--ink-3)' }}>
                                        <input type="checkbox" checked={proj.billable === false}
                                               onChange={e => setBillable(proj.id, !e.target.checked)}
                                               style={{ accentColor: 'var(--warn)', cursor: 'pointer' }}/>
                                        nicht abrechenbar
                                      </label>
                                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 11, color: 'var(--ink-3)' }}>
                                        <input type="checkbox" checked={!!proj.invoiced}
                                               onChange={e => setInvoiced(proj.id, e.target.checked)}
                                               style={{ accentColor: 'var(--ok)', cursor: 'pointer' }}/>
                                        abgerechnet
                                      </label>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Revenue per employee */}
          <div className="card col-5 card-pad-lg">
            <div className="card-title">Umsatz pro Mitarbeiter</div>
            {perEmployee.length === 0 ? (
              <div className="tiny muted" style={{ marginTop: 14 }}>Keine Daten.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                {perEmployee.map(e => (
                  <div key={e.profile.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar user={e.profile} size={32}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {capitalizeName(e.profile.full_name) || e.profile.email || '—'}
                        </span>
                        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 600 }}>{fmtEuro(openEmp(e))}</span>
                          {e.invoicedEuro > 0 && (
                            <span className="mono tiny" style={{ color: 'var(--ok)' }} title="bereits abgerechnet">+{fmtEuro(e.invoicedEuro)} abgerechnet</span>
                          )}
                          {e.internalEuro > 0 && (
                            <span className="mono tiny muted" title="nicht abrechenbar">+{fmtEuro(e.internalEuro)} intern</span>
                          )}
                        </span>
                      </div>
                      <div className="bar" style={{ marginTop: 6 }}>
                        <span style={{ width: `${(openEmp(e) / maxEmpEuro) * 100}%` }}/>
                      </div>
                      <div className="tiny muted" style={{ marginTop: 4 }}>{fmtMin(e.minutes)} · {fmtEuro(e.rate)}/h</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Completed projects in period */}
        <div className="card card-pad-lg">
          <div className="card-title">Abgeschlossene Projekte <span className="sub">in {monthLabel(ym.year, ym.month)}</span></div>
          {completed.length === 0 ? (
            <div className="tiny muted" style={{ marginTop: 14 }}>In diesem Monat wurde kein Projekt abgeschlossen.</div>
          ) : (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {completed.map(row => {
                const flagged = row.onTime === false || row.overBudget;
                return (
                  <div key={row.project.id} style={{
                    padding: '10px 12px', borderRadius: 10,
                    background: 'var(--bg-sunken)',
                    border: `1px solid ${flagged ? 'rgba(239,68,68,0.2)' : 'var(--stroke)'}`,
                  }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <ProjectAvatar project={row.project} size={34}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.project.title}</div>
                      <div className="tiny muted" style={{ marginTop: 2 }}>
                        {row.customer?.name || 'Ohne Kunde'} · abgeschlossen {fmtDeadline(row.completedAt)}
                        {row.deadline && <> · Deadline {fmtDeadline(row.deadline)}</>}
                      </div>
                    </div>
                    {/* On-time chip */}
                    {row.onTime == null ? (
                      <span className="chip" style={{ height: 24 }}>keine Deadline</span>
                    ) : row.onTime ? (
                      <span className="chip chip-ok" style={{ height: 24 }}><IconCheck size={11}/> pünktlich</span>
                    ) : (
                      <span className="chip chip-crit" style={{ height: 24 }}><IconX size={11}/> {row.daysLate} Tage spät</span>
                    )}
                    {/* Budget chip */}
                    {row.budgetPct != null && (
                      <span className={`chip ${row.overBudget ? 'chip-crit' : 'chip-ok'}`} style={{ height: 24 }}>
                        {row.budgetPct}% Budget
                      </span>
                    )}
                    <button className="btn btn-ghost" onClick={() => feedbackForProject(row)} title="Feedback ans Team geben">
                      <IconMessage size={13}/> Feedback
                    </button>
                   </div>
                   <ClosureReasons projectId={row.project.id}/>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {feedbackCtx && (
        <FeedbackModal
          {...feedbackCtx}
          onClose={() => setFeedbackCtx(null)}
          onSent={() => setFeedbackCtx(null)}
        />
      )}
    </>
  );
}

export { AnalyticsPage };
